import { Document, Image, Page, StyleSheet, Text, View } from "@react-pdf/renderer"

import { IMAGE_GAP_PT, type GanttPdfImageLayout } from "./gantt-pdf-constants.ts"
import {
  MILESTONE_STATUS_ORDER,
  MILESTONE_STATUSES,
  MILESTONE_STATUSES_MAP,
  type MilestoneStatuses
} from "../../../../../constants/milestone-statuses.ts"

const styles = StyleSheet.create({
  page: { padding: 24, fontSize: 10, fontFamily: "Helvetica", color: "#1f2937" },
  title: { fontSize: 16, fontWeight: 700, marginBottom: 2 },
  subtitle: { fontSize: 11, color: "#6b7280", marginBottom: 2 },
  meta: { fontSize: 9, color: "#9ca3af", marginBottom: 12 },
  legendRow: { flexDirection: "row", flexWrap: "wrap", marginBottom: 14 },
  legendItem: { flexDirection: "row", alignItems: "center", marginRight: 12 },
  legendDot: { width: 6, height: 6, borderRadius: 3, marginRight: 4 },
  legendLabel: { fontSize: 9, color: "#4b5563" },
  chartRow: { flexDirection: "row" }
})

const LEGEND_STATUSES = MILESTONE_STATUS_ORDER

// @react-pdf/renderer's Text has no line-clamp prop, so an unusually long
// project name is truncated here instead — otherwise it can wrap the
// subtitle onto extra lines and grow the header past its reserved budget.
// Splits on Unicode code points (Array.from), not UTF-16 code units
// (.slice) — a project name with an astral character (e.g. an emoji) at
// the cut point would otherwise split its surrogate pair in half.
const MAX_SUBTITLE_LENGTH = 60
export function truncateForSubtitle(projectName: string): string {
  const codePoints = Array.from(projectName)
  return codePoints.length > MAX_SUBTITLE_LENGTH
    ? `${codePoints.slice(0, MAX_SUBTITLE_LENGTH - 1).join("")}…`
    : projectName
}

// Hardcoded English labels for PDF display (not i18n'd; @react-pdf/renderer
// has no access to I18nextProvider and the PDF design intentionally treats
// all text as English, matching other hardcoded strings like "Generated on...").
// Typed as Record<MilestoneStatuses, string> (every status, not just
// LEGEND_STATUSES's current members) so a future added status is a compile
// error here instead of a silently-missing legend row.
const STATUS_LABELS: Record<MilestoneStatuses, string> = {
  [MILESTONE_STATUSES.PLANNED]: "Planned",
  [MILESTONE_STATUSES.PENDING]: "Pending",
  [MILESTONE_STATUSES.IN_PROGRESS]: "In Progress",
  [MILESTONE_STATUSES.COMPLETED]: "Completed",
  [MILESTONE_STATUSES.BLOCKED]: "Blocked"
}

export { LEGEND_STATUSES, STATUS_LABELS }

export interface GanttPdfPageImages {
  leftPanelDataUrl: string
  chartDataUrl: string
  layout: GanttPdfImageLayout
}

export interface GanttPdfDocumentProps {
  projectName?: string
  // Pre-formatted in the account's own date format -- see the comment on
  // `buildGanttPdfBlob`'s `generatedAtLabel` param.
  generatedAtLabel: string
  /** One per page: the same whole-span timeline, a slice of the rows. */
  pages: GanttPdfPageImages[]
}

export function GanttPdfDocument({ projectName, generatedAtLabel, pages }: GanttPdfDocumentProps) {
  return (
    <Document>
      {pages.map(({ leftPanelDataUrl, chartDataUrl, layout }, index) => (
        <Page key={index} size="A4" orientation="landscape" style={styles.page}>
          <Text style={styles.title}>Milestone Schedule</Text>
          {projectName && <Text style={styles.subtitle}>{truncateForSubtitle(projectName)}</Text>}
          <Text style={styles.meta}>
            Generated on {generatedAtLabel}
            {pages.length > 1 ? ` · Page ${index + 1} of ${pages.length}` : ""}
          </Text>

          <View style={styles.legendRow}>
            {LEGEND_STATUSES.map((status) => (
              <View key={status} style={styles.legendItem}>
                <View
                  style={{
                    ...styles.legendDot,
                    backgroundColor: MILESTONE_STATUSES_MAP[status]?.style?.color || "#9ca3af"
                  }}
                />
                <Text style={styles.legendLabel}>{STATUS_LABELS[status]}</Text>
              </View>
            ))}
            <View style={styles.legendItem}>
              <Text style={styles.legendLabel}>Shifted by a dependency</Text>
            </View>
          </View>

          <View style={styles.chartRow}>
            <Image
              src={leftPanelDataUrl}
              style={{ width: layout.leftPanelWidth, height: layout.leftPanelHeight }}
            />
            <Image
              src={chartDataUrl}
              style={{
                width: layout.chartWidth,
                height: layout.chartHeight,
                marginLeft: IMAGE_GAP_PT
              }}
            />
          </View>
        </Page>
      ))}
    </Document>
  )
}
